import { AGE_BANDS, GUEST_LIKELIHOODS, GUEST_SIDES, RSVP_STATUSES } from './constants.js';
import type { AgeBand, GuestSide, RsvpStatus } from './constants.js';
import type { Guest, GuestStats, Household } from './schemas/guest.js';

/**
 * The guest-list rules, as pure functions shared by the API and the web client.
 *
 * They live here because the headcount rule used to exist twice — once in the
 * API's summary service and once, character for character, in the Guests tab —
 * with nothing keeping them honest. One copy, two callers.
 */

/** RSVP states that still occupy a chair: nobody has said no. */
const MIGHT_COME: readonly RsvpStatus[] = ['attending', 'maybe', 'pending'];

const zeroed = <K extends string>(keys: readonly K[]): Record<K, number> =>
  Object.fromEntries(keys.map((k) => [k, 0])) as Record<K, number>;

export function computeGuestStats(guests: Guest[]): GuestStats {
  const byRsvp = zeroed(RSVP_STATUSES);
  const bySide = zeroed(GUEST_SIDES);
  const byAgeBand = zeroed(AGE_BANDS);
  const byLikelihood = zeroed(GUEST_LIKELIHOODS);
  const attendingByAgeBand = zeroed(AGE_BANDS);
  const byTag: Record<string, number> = {};

  let named = 0;
  let placeholders = 0;
  let confirmedHeadcount = 0;
  let anticipatedHeadcount = 0;
  let expectedHeadcount = 0;

  /** Per household: does anyone still owe an answer. */
  const pendingByHousehold = new Map<string, boolean>();

  for (const g of guests) {
    const rsvp = g.rsvp ?? 'pending';
    const side = g.side ?? 'shared';
    // A legacy row predates age bands; it is an adult until someone says otherwise.
    const ageBand = g.ageBand ?? 'adult';
    // Likewise: a row written before this field existed is simply unjudged.
    const likelihood = g.likelihood ?? 'unsure';

    byRsvp[rsvp] += 1;
    bySide[side] += 1;
    byAgeBand[ageBand] += 1;
    for (const tag of g.tags ?? []) byTag[tag] = (byTag[tag] ?? 0) + 1;

    if (g.isPlaceholder) placeholders += 1;
    else named += 1;

    if (rsvp === 'attending') {
      confirmedHeadcount += 1;
      attendingByAgeBand[ageBand] += 1;
    }
    if (MIGHT_COME.includes(rsvp)) {
      anticipatedHeadcount += 1;
      // Somebody who has actually said yes counts however unlikely you thought
      // they were — the guess is for people who have not answered, and keeping
      // it after they have would let a stale note overrule a real reply.
      if (rsvp === 'attending' || likelihood !== 'unlikely') expectedHeadcount += 1;
    }
    byLikelihood[likelihood] += 1;

    if (g.householdId) {
      const outstanding = pendingByHousehold.get(g.householdId) ?? false;
      pendingByHousehold.set(g.householdId, outstanding || rsvp === 'pending');
    }
  }

  let householdsFullyResponded = 0;
  for (const outstanding of pendingByHousehold.values()) {
    if (!outstanding) householdsFullyResponded += 1;
  }

  return {
    total: guests.length,
    named,
    placeholders,
    households: pendingByHousehold.size,
    byRsvp,
    bySide,
    byAgeBand,
    byTag,
    confirmedHeadcount,
    anticipatedHeadcount,
    expectedHeadcount,
    byLikelihood,
    attendingByAgeBand,
    householdsFullyResponded,
  };
}

/** A household whose item is missing, so its people can still be shown. */
export interface SyntheticHousehold {
  householdId: string;
  label: string;
  synthetic: true;
}

export interface HouseholdGroup {
  household: Household | SyntheticHousehold;
  members: Guest[];
}

/**
 * Adults first, then children, then infants, alphabetical within a band — the
 * order a seating plan and a place-card list both want.
 */
export function sortHouseholdMembers(members: Guest[]): Guest[] {
  const rank = (g: Guest) => AGE_BANDS.indexOf(g.ageBand ?? 'adult');
  return [...members].sort((a, b) => {
    const byBand = rank(a) - rank(b);
    if (byBand !== 0) return byBand;
    return `${a.lastName ?? ''} ${a.firstName}`.localeCompare(`${b.lastName ?? ''} ${b.firstName}`);
  });
}

/**
 * A label for a household nobody bothered to name: the shared surname where
 * there is one, otherwise the first names.
 */
export function householdLabelFor(
  members: { firstName: string; lastName?: string }[],
  fallback = 'Household',
): string {
  const people = members.filter((m) => m.firstName.trim().length > 0);
  if (people.length === 0) return fallback;

  // `filter(Boolean)` here was a real bug: it dropped the people who have no
  // surname, so a couple where only the second person had one looked like a
  // household that all shared it — and got named after them. Importing
  // "Alex Rivera" with a plus-one "Maria Lopez" produced "Lopez household",
  // naming the invitation after the guest rather than the person invited.
  // Everyone must have a surname, and it must be the same one.
  const surnames = people.map((m) => (m.lastName ?? '').trim());
  const shared = new Set(surnames);
  if (people.length > 1 && surnames.every(Boolean) && shared.size === 1) {
    return `${surnames[0] ?? ''} household`;
  }
  if (people.length === 1) {
    const only = people[0];
    return only ? [only.firstName, only.lastName].filter(Boolean).join(' ') : fallback;
  }
  const names = people.slice(0, 3).map((m) => m.firstName);
  const rest = people.length - names.length;
  const joined = names.length === 2 ? names.join(' & ') : names.join(', ');
  return rest > 0 ? `${joined} +${rest}` : joined;
}

/** "Alex's guest" for a placeholder, a real name otherwise. */
export function guestDisplayName(g: Guest, hostName?: string): string {
  if (g.isPlaceholder) return hostName ? `${hostName}'s guest` : 'Guest';
  return [g.firstName, g.lastName].filter(Boolean).join(' ');
}

/**
 * Groups guests under their household for the table.
 *
 * A guest whose household item is missing still comes back, under a synthetic
 * group — a person must never disappear because a grouping row did.
 */
export function groupByHousehold(guests: Guest[], households: Household[]): HouseholdGroup[] {
  const byId = new Map(households.map((h) => [h.householdId, h]));
  const members = new Map<string, Guest[]>();
  const orphans: Guest[] = [];

  for (const g of guests) {
    if (!g.householdId) {
      orphans.push(g);
      continue;
    }
    const list = members.get(g.householdId);
    if (list) list.push(g);
    else members.set(g.householdId, [g]);
  }

  const groups: HouseholdGroup[] = [];
  for (const [householdId, list] of members) {
    const household = byId.get(householdId);
    groups.push({
      household: household ?? {
        householdId,
        label: householdLabelFor(list),
        synthetic: true,
      },
      members: sortHouseholdMembers(list),
    });
  }

  // A guest with no household at all: still one row, still visible.
  for (const g of orphans) {
    groups.push({
      household: { householdId: g.guestId, label: householdLabelFor([g]), synthetic: true },
      members: [g],
    });
  }

  return groups.sort((a, b) => a.household.label.localeCompare(b.household.label));
}

/** True when the household is one person who is not a placeholder — render it plainly. */
export function isSolo(group: HouseholdGroup): boolean {
  return group.members.length === 1 && !group.members[0]?.isPlaceholder;
}

/** Counted against a plan's guest cap: real people, not unnamed placeholders. */
export function billableGuestCount(guests: Guest[]): number {
  return guests.filter((g) => !g.isPlaceholder).length;
}

export type { AgeBand, GuestSide, RsvpStatus };
